import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { toast } from "sonner";
import { MonthPicker } from "@/components/MonthPicker";
import { PageHeader } from "@/components/PageHeader";
import { SearchInput } from "@/components/SearchInput";
import { SelectionBar } from "@/components/SelectionBar";
import { StatusDot } from "@/components/StatusDot";
import { EmptyRow, SkeletonRows } from "@/components/TableStates";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClassifyDialog } from "@/features/expenses/ClassifyDialog";
import { get, post } from "@/lib/api";
import { allClientsQuery } from "@/lib/clients";
import { currentMonth, date, eur, monthLabel } from "@/lib/format";
import { matchesExpense, readExpenseFilters, writeExpenseFilters, type ExpenseFilters } from "@/lib/filters";
import { assignmentText, informativePurpose } from "@/lib/shareLines";
import type { ExpenseRow, ExpensesOutput, SyncResult } from "@/lib/types";
import { cn } from "@/lib/utils";

function Assignment({ row }: { row: ExpenseRow }) {
  if (row.classification === null) return <StatusDot tone="open">offen</StatusDot>;
  if (row.classification === "general") return <StatusDot tone="neutral">Allgemein</StatusDot>;
  return <StatusDot tone="done" className="text-foreground">{assignmentText(row)}</StatusDot>;
}

const COLS = 6;

export function Ausgaben() {
  const [sp, setSp] = useSearchParams();
  const f = readExpenseFilters(sp);
  const setF = (patch: Partial<ExpenseFilters>) => setSp(writeExpenseFilters({ ...f, ...patch }), { replace: true });
  const qc = useQueryClient();

  const expenses = useQuery({
    queryKey: ["expenses", f.status, f.month],
    queryFn: () => get<ExpensesOutput>(`/api/expenses?status=${f.status}${f.month ? `&month=${f.month}` : ""}`),
  });
  // All clients incl. former ones, so existing shares still show their client; the dialog's pickers hide former ones.
  const clients = useQuery(allClientsQuery);
  const rows = useMemo(() => (expenses.data?.rows ?? []).filter((r) => matchesExpense(r, f.q)), [expenses.data, f.q]);
  const [selection, setSelection] = useState<Set<number>>(new Set());
  const [classifying, setClassifying] = useState<ExpenseRow[]>([]);
  const selected = rows.filter((r) => selection.has(r.sevdesk_transaction_id));
  const clear = () => setSelection(new Set());
  const toggle = (id: number) => setSelection((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // The variable is the "manual" flag: a manual sync always toasts, the automatic one only when something new arrived.
  const sync = useMutation<SyncResult, Error, boolean>({
    mutationFn: () => post<SyncResult>("/api/expenses/sync"),
    onSuccess: (r, manual) => {
      qc.invalidateQueries({ queryKey: ["expenses"] });
      if (manual || r.inserted > 0) toast.success(`${r.inserted} neu · ${r.autoClassified} per Regel · ${r.open} offen`);
    },
    onError: (e) => toast.error(`Abgleich fehlgeschlagen: ${e.message}`),
  });
  const synced = useRef(false);
  useEffect(() => { if (!synced.current) { synced.current = true; sync.mutate(false); } }, []);

  const total = rows.reduce((s, r) => s + r.amount, 0);
  const loading = expenses.isPending;
  const period = f.month ? `im ${monthLabel(f.month)}` : "";
  const emptyText = f.q ? `Keine Ausgaben zu „${f.q}“.`
    : f.status === "open" ? `Keine offenen Ausgaben ${period}. Alles zugeordnet.`
    : `Keine Ausgaben ${period}.`;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Ausgaben"
        actions={
          <>
            <Button variant="ghost" size="sm" asChild><Link to="/ausgaben/verrechnung">Verrechnung</Link></Button>
            <Button variant="ghost" size="sm" asChild><Link to="/ausgaben/regeln">Regeln</Link></Button>
            <Button variant="outline" size="sm" disabled={sync.isPending} onClick={() => sync.mutate(true)}>
              <RefreshCw className={cn(sync.isPending && "motion-safe:animate-spin")} />
              Abgleichen
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={f.status} onValueChange={(v) => { clear(); setF({ status: v as ExpenseFilters["status"] }); }}>
          <TabsList aria-label="Status">
            <TabsTrigger value="open">Offen</TabsTrigger>
            <TabsTrigger value="classified">Zugeordnet</TabsTrigger>
            <TabsTrigger value="all">Alle</TabsTrigger>
          </TabsList>
        </Tabs>
        <MonthPicker value={f.month} fallback={currentMonth()} onChange={(month) => { clear(); setF({ month }); }} />
        <SearchInput className="w-full sm:ml-auto sm:w-60" value={f.q} onChange={(q) => setF({ q })} />
      </div>

      <dl className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
        <div><dt className="text-muted-foreground">Summe</dt><dd className="text-lg font-semibold tabular-nums">{loading ? "–" : eur(total)}</dd></div>
        <div><dt className="text-muted-foreground">Ausgaben</dt><dd className="text-lg tabular-nums text-muted-foreground">{loading ? "–" : rows.length}</dd></div>
      </dl>

      {expenses.isError && <p className="text-sm text-destructive">{expenses.error.message}</p>}

      <div className="hidden md:block">
        <Table className="table-fixed" containerClassName="overflow-visible">
          <colgroup>
            <col style={{ width: "2.75rem" }} /><col style={{ width: "6.5rem" }} /><col style={{ width: "24%" }} />
            <col /><col style={{ width: "7rem" }} /><col style={{ width: "24%" }} />
          </colgroup>
          <TableHeader>
            <TableRow className="hover:bg-transparent [&>th]:sticky [&>th]:top-14 [&>th]:z-10 [&>th]:bg-background [&>th]:text-muted-foreground [&>th]:shadow-[inset_0_-1px_0_var(--border)]">
              <TableHead />
              <TableHead>Datum</TableHead>
              <TableHead>Empfänger</TableHead>
              <TableHead>Verwendungszweck</TableHead>
              <TableHead className="text-right">Betrag</TableHead>
              <TableHead>Zuordnung</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? <SkeletonRows columns={COLS} /> : rows.length === 0 ? <EmptyRow columns={COLS}>{emptyText}</EmptyRow> : rows.map((r) => {
              const id = r.sevdesk_transaction_id;
              const purpose = informativePurpose(r);
              return (
                <TableRow
                  key={id}
                  data-state={selection.has(id) ? "selected" : undefined}
                  tabIndex={0}
                  aria-label={`Ausgabe ${r.payee} vom ${date(r.value_date)} zuordnen`}
                  onClick={() => setClassifying([r])}
                  onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) setClassifying([r]); }}
                  className="h-11 cursor-pointer outline-none focus-visible:bg-muted/60"
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <label className="-my-2 flex h-11 cursor-pointer items-center">
                      <Checkbox aria-label="Auswählen" checked={selection.has(id)} onCheckedChange={() => toggle(id)} />
                    </label>
                  </TableCell>
                  <TableCell className="tabular-nums">{date(r.value_date)}</TableCell>
                  <TableCell className="overflow-hidden">
                    <span className="flex items-center gap-2">
                      <span className="truncate" title={r.payee}>{r.payee}</span>
                      {r.source === "rule" && <Badge variant="secondary" className="shrink-0">Regel</Badge>}
                    </span>
                  </TableCell>
                  <TableCell className="overflow-hidden text-muted-foreground">
                    {purpose && <span className="block truncate" title={purpose}>{purpose}</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{eur(r.amount)}</TableCell>
                  <TableCell className="overflow-hidden"><Assignment row={r} /></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <ul className="space-y-2 md:hidden">
        {loading && Array.from({ length: 5 }, (_, i) => <li key={i} className="h-20 animate-pulse rounded-lg bg-muted motion-reduce:animate-none" />)}
        {!loading && rows.length === 0 && <li className="py-12 text-center text-sm text-muted-foreground">{emptyText}</li>}
        {!loading && rows.map((r) => {
          const id = r.sevdesk_transaction_id;
          return (
            <li key={id} className={cn("flex rounded-lg border", selection.has(id) && "bg-muted")}>
              <label className="flex w-11 shrink-0 cursor-pointer justify-center pt-3.5">
                <Checkbox aria-label="Auswählen" checked={selection.has(id)} onCheckedChange={() => toggle(id)} />
              </label>
              <button type="button" onClick={() => setClassifying([r])} className="min-w-0 flex-1 space-y-1 py-3 pr-3 text-left text-sm">
                <div className="flex justify-between gap-3">
                  <span className="truncate font-medium">{r.payee}</span>
                  <span className="shrink-0 tabular-nums">{eur(r.amount)}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span className="shrink-0 tabular-nums">{date(r.value_date)}</span>
                  <span aria-hidden>·</span>
                  <Assignment row={r} />
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      <SelectionBar count={selected.length} onClear={clear}>
        <Button size="sm" onClick={() => setClassifying(selected)}>Zuordnen…</Button>
      </SelectionBar>

      <ClassifyDialog
        rows={classifying}
        clients={clients.data?.clients ?? []}
        defaultMarginPct={expenses.data?.defaultMarginPct ?? 25}
        onClose={() => { setClassifying([]); clear(); }}
      />
    </div>
  );
}
